import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { AlertCircle } from 'lucide-react';
import { Label } from './ui/label';
import { Input } from './ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatTime = (t) => new Date(`2000-01-01T${t}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
const toMin = (t) => { const [h, m] = (t || '00:00').split(':').map(Number); return h * 60 + m; };
const fromMin = (n) => `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;
const hrs = (mins) => Math.round((mins / 60) * 100) / 100;

const lunchWindow = (policy) => {
  const dur = policy && policy.feature_enabled && policy.lunch_enabled ? (policy.lunch_duration || 0) : 0;
  const ls = toMin((policy && policy.lunch_start_window) || '12:00');
  return { ls, le: ls + dur, dur };
};

// Mirrors backend pto_ranges_for_worked: shift minus worked window, unpaid lunch carved out.
const ptoRanges = (os, oe, ws, we, { ls, le, dur }) => {
  const left = [];
  if (os < ws) left.push([os, Math.min(ws, oe)]);
  if (we < oe) left.push([Math.max(we, os), oe]);
  const out = [];
  left.forEach(([a, b]) => {
    if (dur && ls < b && le > a) {
      if (a < ls) out.push([a, ls]);
      if (le < b) out.push([le, b]);
    } else out.push([a, b]);
  });
  return out.filter(([a, b]) => a < b);
};

const mergeAcrossLunch = (ranges, { ls, le, dur }) => ranges.reduce((out, [a, b]) => {
  const last = out[out.length - 1];
  if (last && dur && last[1] === ls && a === le) out[out.length - 1] = [last[0], b, true];
  else out.push([a, b, false]);
  return out;
}, []);

const overlap = (a, b, c, d) => Math.max(0, Math.min(b, d) - Math.max(a, c));

export const PartialPTOFields = ({ ptoForm, setPtoForm, userId, lunchPolicy, onValidityChange }) => {
  const [shift, setShift] = useState(undefined); // undefined=loading, null=no shift
  const touched = useRef(false);

  useEffect(() => {
    if (!userId || !ptoForm.start_date) { setShift(null); return; }
    setShift(undefined);
    axios.get(`${API}/schedules`, {
      params: { date_from: ptoForm.start_date, date_to: ptoForm.start_date },
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    }).then(res => {
      const s = (res.data || [])
        .filter(x => x.user_id === userId && x.date === ptoForm.start_date && !['pto', 'pending_pto'].includes(x.shift_type))
        .sort((a, b) => a.start_time.localeCompare(b.start_time))[0];
      if (!s) { setShift(null); return; }
      setShift({ start: s.start_time, end: s.end_time });
      const { ls } = lunchWindow(lunchPolicy);
      setPtoForm(prev => ({
        ...prev,
        start_time: s.start_time,
        end_time: s.end_time,
        ...(touched.current ? {} : { worked_start_time: s.start_time, worked_end_time: fromMin(Math.min(ls, toMin(s.end_time))) })
      }));
    }).catch(() => setShift(null));
  }, [userId, ptoForm.start_date, lunchPolicy, setPtoForm]);

  const lunch = lunchWindow(lunchPolicy);
  let error = null;
  let workedH = 0;
  let ptoH = 0;
  let ranges = [];
  if (shift) {
    const os = toMin(shift.start), oe = toMin(shift.end);
    const ws = toMin(ptoForm.worked_start_time), we = toMin(ptoForm.worked_end_time);
    if (!ptoForm.worked_start_time || !ptoForm.worked_end_time || ws >= we) error = 'Work end time must be after the start time.';
    else if (ws < os || we > oe) error = `Work hours must be within the scheduled shift (${formatTime(shift.start)} – ${formatTime(shift.end)}).`;
    else {
      ranges = ptoRanges(os, oe, ws, we, lunch);
      ptoH = hrs(ranges.reduce((t, [a, b]) => t + (b - a), 0));
      workedH = hrs(we - ws - (lunch.dur ? overlap(ws, we, lunch.ls, lunch.le) : 0));
      if (ptoH <= 0) error = 'These work hours cover the whole shift, so there is no PTO to take.';
    }
  } else if (shift === null) {
    error = 'No scheduled shift found on this date, so PTO hours can\'t be worked out. Ask your manager to schedule the shift first.';
  }

  useEffect(() => { onValidityChange(!error && !!shift); }, [error, shift, onValidityChange]);

  const setWorked = (field, value) => { touched.current = true; setPtoForm(prev => ({ ...prev, [field]: value })); };

  return (
    <div className="p-3 bg-indigo-50 rounded-lg space-y-3" data-testid="partial-pto-fields">
      <p className="text-xs text-gray-600" data-testid="partial-pto-shift-info">
        {shift === undefined ? 'Loading scheduled shift…'
          : shift ? <>Scheduled shift: <strong>{formatTime(shift.start)} – {formatTime(shift.end)}</strong>. Set the hours you <strong>work</strong>; PTO covers the rest (lunch stays unpaid).</>
          : 'No scheduled shift on this date.'}
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-sm font-medium">Works From</Label>
          <Input type="time" data-testid="pto-worked-start" value={ptoForm.worked_start_time} onChange={(e) => setWorked('worked_start_time', e.target.value)} className="mt-1" />
        </div>
        <div>
          <Label className="text-sm font-medium">Works Until</Label>
          <Input type="time" data-testid="pto-worked-end" value={ptoForm.worked_end_time} onChange={(e) => setWorked('worked_end_time', e.target.value)} className="mt-1" />
        </div>
      </div>
      {error ? (
        <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2" data-testid="partial-pto-error">
          <AlertCircle className="h-4 w-4 flex-shrink-0" /><span>{error}</span>
        </div>
      ) : shift && (
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="bg-white rounded-md px-3 py-2 border border-indigo-100">
            <p className="text-xs text-gray-500">Working</p>
            <p className="font-semibold text-gray-900" data-testid="pto-worked-hours">{workedH} hours</p>
          </div>
          <div className="bg-white rounded-md px-3 py-2 border border-indigo-100">
            <p className="text-xs text-gray-500">PTO (paid time off)</p>
            <p className="font-semibold text-indigo-700" data-testid="pto-hours-confirm">{ptoH} hours</p>
            <p className="text-[11px] text-gray-500" data-testid="pto-ranges">{mergeAcrossLunch(ranges, lunch).map(([a, b, hl]) => `${formatTime(fromMin(a))} – ${formatTime(fromMin(b))}${hl ? ` (unpaid lunch ${formatTime(fromMin(lunch.ls))} – ${formatTime(fromMin(lunch.le))})` : ''}`).join(', ')}</p>
          </div>
        </div>
      )}
    </div>
  );
};
